import React from 'react';
import { Link } from 'react-router-dom';
import { BRAND_NAME } from '../../config/brand';
import { cn } from '../../utils/cn';

/** Official BeCoder mark — "The Channel" (flat geometric, no letter tiles). */
export const BrandMark: React.FC<{
  className?: string;
}> = ({ className }) => {
  return (
    <svg
      viewBox="0 0 32 32"
      className={cn('shrink-0', className)}
      aria-hidden
      focusable="false"
    >
      <path fill="#FF4F00" d="M2 3h9v26H2V3zm13 0 15 13-15 13V3z" />
    </svg>
  );
};

interface BrandLogoProps {
  to?: string | null;
  className?: string;
  markClassName?: string;
  showWordmark?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** On dark surfaces use paper wordmark + signal mark */
  invert?: boolean;
}

/**
 * Official BeCoder logo lockup.
 * Mark = The Channel. Wordmark = Syne/Manrope via CSS font-display.
 */
export const BrandLogo: React.FC<BrandLogoProps> = ({
  to = '/',
  className,
  markClassName,
  showWordmark = true,
  size = 'md',
  invert = false,
}) => {
  const markSize = size === 'lg' ? 'h-9 w-9' : size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';
  const textSize = size === 'lg' ? 'text-[1.35rem]' : size === 'sm' ? 'text-sm' : 'text-base';
  const gap = size === 'lg' ? 'gap-3' : size === 'sm' ? 'gap-2' : 'gap-2.5';

  const inner = (
    <span className={cn('inline-flex items-center group', gap, className)}>
      <BrandMark className={cn(markSize, 'bc-brand-mark', markClassName)} />
      {showWordmark && (
        <span
          className={cn(
            'font-display font-bold tracking-tight leading-none',
            invert ? 'text-paper' : 'text-ink dark:text-paper',
            textSize
          )}
        >
          {BRAND_NAME}
        </span>
      )}
    </span>
  );

  if (to === null || to === undefined) return inner;
  return (
    <Link
      to={to}
      className="inline-flex hover:opacity-80 transition-opacity duration-200 ease-out"
      aria-label={BRAND_NAME}
    >
      {inner}
    </Link>
  );
};

/** @deprecated Use BrandLogo — kept as alias during migration */
export const BrandWordmark = BrandLogo;
